import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SchemaCompareEndpoint, SchemaCompareOptions } from '../../core/types/workspaceTypes';
import { createGuardedJsonStorage } from '../../../../core/storage/guardedJsonStorage';
import { showPersistWriteFailureToast } from '../../../../components/ui/persistWriteFailureToast';

const MAX_RECENT_ENTRIES = 20;

export type RecentSchemaCompareEntry = {
  /** Stable identifier derived from the endpoint tuple — used for React keys and removal. */
  id: string;
  source: SchemaCompareEndpoint;
  target: SchemaCompareEndpoint;
  compareWholeDatabase: boolean;
  /** Snapshotted display values so the entry still renders if a connection is later renamed/removed. */
  sourceConnectionName?: string;
  sourceServerHost?: string;
  targetConnectionName?: string;
  targetServerHost?: string;
  comparedAt: Date;
  /**
   * Snapshotted compare options (depth, object types, ignore rules) — added
   * W5.1 (F02), optional/additive so entries written before this change
   * simply lack the key. `SchemaCompareSetupDialog.handleSelectRecent`
   * treats `undefined` as "leave the dialog's current options untouched",
   * which is exactly pre-W5.1 behaviour — never crashes and never applies a
   * half-populated options object.
   */
  options?: SchemaCompareOptions;
};

export type RecordRecentArgs = Omit<RecentSchemaCompareEntry, 'id' | 'comparedAt'>;

type RecentSchemaCompareState = {
  entries: RecentSchemaCompareEntry[];
  record: (args: RecordRecentArgs) => void;
  remove: (id: string) => void;
  clear: () => void;
};

function encodeFilter(f: SchemaCompareEndpoint['schemaFilter']): string {
  if (!f || f.schemas.length === 0) return '';
  const sortedSchemas = [...f.schemas].sort().join(',');
  return `${f.mode}:${sortedSchemas}`;
}

function buildEntryId(args: RecordRecentArgs): string {
  const parts = [
    args.source.connectionId ?? '',
    args.source.database ?? '',
    args.source.objectType ?? '',
    args.source.objectSchema ?? '',
    args.source.objectName ?? '',
    encodeFilter(args.source.schemaFilter),
    args.target.connectionId ?? '',
    args.target.database ?? '',
    args.target.objectType ?? '',
    args.target.objectSchema ?? '',
    args.target.objectName ?? '',
    encodeFilter(args.target.schemaFilter),
    args.compareWholeDatabase ? 'whole' : 'object',
  ];
  return parts.join('|');
}

export const useRecentSchemaCompareStore = create<RecentSchemaCompareState>()(
  persist(
    (set) => ({
      entries: [],

      record: (args) => {
        // Need both source and target connection + database to be meaningful
        if (!args.source.connectionId || !args.source.database) return;
        if (!args.target.connectionId || !args.target.database) return;

        const id = buildEntryId(args);
        const entry: RecentSchemaCompareEntry = {
          ...args,
          id,
          comparedAt: new Date(),
        };

        set((state) => {
          const without = state.entries.filter((e) => e.id !== id);
          return { entries: [entry, ...without].slice(0, MAX_RECENT_ENTRIES) };
        });
      },

      remove: (id) => {
        set((state) => ({ entries: state.entries.filter((e) => e.id !== id) }));
      },

      clear: () => {
        set({ entries: [] });
      },
    }),
    {
      name: 'sql-studio-recent-schema-compare',
      storage: createGuardedJsonStorage({ onFailure: showPersistWriteFailureToast('your recent schema comparisons', { plural: true }) }),
      // W5.1 (F02): entries gained `options`. Purely additive — deliberately
      // NO version bump. zustand's persist middleware treats a version
      // mismatch with no `migrate` function as unrecoverable and drops the
      // persisted state entirely (see `recentSchemaCompareStore.test.ts`'s
      // "does not drop persisted entries across a hydrate" test) — bumping
      // here would silently wipe every existing user's "Recently compared"
      // history on upgrade. Mirrors the sibling store
      // (`recentDataCompareStore.ts`), which added its own optional
      // `options` field the same way, with no version bump either.
      partialize: (state) => ({ entries: state.entries }),
      onRehydrateStorage: () => (state) => {
        if (state?.entries) {
          state.entries = state.entries.map((e) => ({
            ...e,
            comparedAt: new Date(e.comparedAt),
          }));
        }
      },
    },
  ),
);
